// Libs
import { useEffect, useState } from 'react';
import RSocketWebSocketClient from 'rsocket-websocket-client';
import {
  RSocketClient,
  MAX_STREAM_ID,
  JsonSerializer,
  IdentitySerializer,
  BufferEncoders,
  encodeRoute,
  encodeCompositeMetadata,
  MESSAGE_RSOCKET_ROUTING,
  MESSAGE_RSOCKET_AUTHENTICATION,
  MESSAGE_RSOCKET_COMPOSITE_METADATA,
} from 'rsocket-core';
import log from 'loglevel';
// App
import { RSocketHookResponse } from './rsocket.hooks.interfaces';
import { KEPP_ALIVE, LIFETIME, DATA_MIME_TYPE, RSOCKET_TRANSPORT_URL, RETRY_DELAY } from './rsocket.hooks.constants';
import { encodeBearerToken } from './rsocket.utils';
import { Session } from '../user-access/auth';

export * from './axios-request.hooks.interfaces';

/**
 * Hook for rsocket connections
 */
const useRSocket = <ResponseData = unknown>(
  messageMapping: string,
  session: Session,
  initialValue?: ResponseData
): RSocketHookResponse<ResponseData> => {
  const [error, setError] = useState(null);
  const [data, setData] = useState(initialValue);
  const [client, setClient] = useState(null);
  const isConnected = client?._connection;

  /**
   * Inits rsocket client
   */
  useEffect(() => {
    setupClient();

    return removeClient;
  }, []);

  /**
   * Manages socket when route mapping change
   */
  useEffect(() => {
    if (!client || !session) return;

    if (messageMapping) {
      connectSocket();
    } else if (!messageMapping) {
      removeClient();
    }
  }, [session, messageMapping, client]);

  const setupClient = () => {
    const client = new RSocketClient({
      serializers: {
        data: JsonSerializer,
        metadata: IdentitySerializer,
      },
      setup: {
        keepAlive: KEPP_ALIVE,
        lifetime: LIFETIME,
        dataMimeType: DATA_MIME_TYPE,
        metadataMimeType: MESSAGE_RSOCKET_COMPOSITE_METADATA.string,
      },
      transport: new RSocketWebSocketClient({ url: RSOCKET_TRANSPORT_URL }, BufferEncoders),
    });

    setClient(client);
  };

  /**
   * Closes rsocket connection
   */
  const removeClient = () => {
    if (!client || !client._connection) return;

    log.debug('[rsocket] Closing connection');

    client.close();
    setClient(null);
  };

  /**
   * Opens a connection with rsocket on server side
   */
  const connectSocket = () => {
    if (client._connection) return;

    log.debug('[rsocket] Opening new connection');

    client.connect().subscribe({
      onComplete: onSocketComplete,
      onError: onSocketError,
    });
  };

  /**
   * Retries to open a connection with rsocket on server side
   * @param client the rsocket client
   */
  const retrySetupClient = (delay: number) => setTimeout(setupClient, delay);

  /**
   * Fired on socket complete
   * @param socket the socket
   * socket provides the rsocket interactions fire/forget, request/response,
   * request/stream, etc as well as methods to close the socket.
   */
  const onSocketComplete = (socket) => {
    if (!messageMapping) return;

    log.debug('[rsocket] Socket ready for mapping ', messageMapping);

    const metadata = encodeCompositeMetadata([
      [MESSAGE_RSOCKET_ROUTING, encodeRoute(messageMapping)],
      [MESSAGE_RSOCKET_AUTHENTICATION, encodeBearerToken(session.accessToken)],
    ]);

    socket.requestStream({ metadata }).subscribe({
      onError: onSocketError,
      onNext: (payload) => {
        log.debug('[rsocket] Received data ', payload.data);

        setData(payload.data);
      },
      onSubscribe: (subscription) => subscription.request(MAX_STREAM_ID),
    });
  };

  /**
   * Fired on socket error
   * @param error the error
   */
  const onSocketError = (error: Error) => {
    setError(error);

    log.error(error);

    removeClient();

    retrySetupClient(RETRY_DELAY);
  };

  if (!messageMapping || !session) return { data: null, isConnected: false, error: null };

  return { data, isConnected, error };
};

export default useRSocket;
